import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { API_URL } from '../services/api';
import toast from '../utils/toast';
import CustomPageExpensesTab from './CustomPageExpensesTab';

const CustomPageSubmissions = () => {
  const { pageId } = useParams();
  const { checkPermission, user } = useAuth();
  const navigate = useNavigate();
  
  const [customPage, setCustomPage] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  const canView = checkPermission('View Custom Page Submissions');
  const canCreate = checkPermission('Create Custom Page Submission');
  const canUpdate = checkPermission('Update Custom Page Submission');
  const canDelete = checkPermission('Delete Custom Page Submission');

  useEffect(() => {
    fetchCustomPage();
    fetchSubmissions();
  }, [pageId, pagination.page, searchTerm, selectedStatus]);

  const fetchCustomPage = async () => {
    try {
      const response = await api.get(`/custom-pages/${pageId}`);
      if (response.data.success) {
        setCustomPage(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching custom page:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });

      if (searchTerm) params.append('search', searchTerm);
      if (selectedStatus) params.append('status', selectedStatus);

      const response = await api.get(`/custom-pages/${pageId}/submissions?${params}`);
      
      if (response.data.success) {
        setSubmissions(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (submission) => {
    try {
      const response = await api.get(`/custom-pages/submissions/${submission._id}`);
      if (response.data.success) {
        setSelectedSubmission(response.data.data);
        setShowDetails(true);
      }
    } catch (error) {
      console.error('Error fetching submission details:', error);
      toast.error('Failed to fetch submission details');
    }
  };

  const handleStatusChange = async (submissionId, newStatus) => {
    try {
      const response = await api.put(`/custom-pages/submissions/${submissionId}`, {
        status: newStatus
      });
      
      if (response.data.success) {
        toast.success('Status updated successfully');
        fetchSubmissions();
        if (selectedSubmission && selectedSubmission._id === submissionId) {
          setSelectedSubmission(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      const response = await api.delete(`/custom-pages/submissions/${submissionId}`);
      
      if (response.data.success) {
        toast.success('Submission deleted successfully');
        fetchSubmissions();
        if (selectedSubmission && selectedSubmission._id === submissionId) {
          setShowDetails(false);
          setSelectedSubmission(null);
        }
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFieldValue = (field, value) => {
    if (!value) return '-';

    switch (field.type) {
      case 'upload':
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className="space-y-1">
              {value.map((file, index) => {
                // Handle different file object structures
                const fileUrl = file.fileUrl || file.url || '';
                const fileName = file.fileName || file.name || file.originalName || 'File';
                
                if (!fileUrl) {
                  return (
                    <div key={index} className="text-sm text-gray-500">
                      {fileName} (No URL available)
                    </div>
                  );
                }
                
                return (
                  <div key={index} className="text-sm text-blue-600 hover:text-blue-800">
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                      {fileName}
                    </a>
                  </div>
                );
              })}
            </div>
          );
        }
        return 'No files uploaded';

      case 'masterdata':
        if (value && typeof value === 'object') {
          return value.clientName || 'N/A';
        }
        return value || '-';

      case 'user':
        if (value && typeof value === 'object') {
          return `${value.firstName} ${value.lastName}`;
        }
        return value || '-';

      case 'date':
        return (value);

      case 'multitext':
        return value.length > 100 ? `${value.substring(0, 100)}...` : value;

      default:
        return value;
    }
  };

  if (loading && submissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!customPage) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Page Not Found</h2>
        <p className="text-gray-600">The requested page could not be found.</p>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to view submissions.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {customPage.name} - Submissions
          </h1>
          <p className="text-gray-600">View and manage form submissions</p>
        </div>
        {canCreate && (
          <button
            onClick={() => navigate(`/custom-page/${pageId}`)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Create New Submission
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search submissions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Submissions List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deadline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {submissions.map((submission) => (
                <tr key={submission._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {submission.submittedBy?.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(submission.status)}`}>
                      {submission.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(submission.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {submission.deadline ? formatDate(submission.deadline) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleViewDetails(submission)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View
                    </button>
                    {canUpdate && (
                      <select
                        value={submission.status}
                        onChange={(e) => handleStatusChange(submission._id, e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="draft">Draft</option>
                        <option value="submitted">Submitted</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="completed">Completed</option>
                      </select>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(submission._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {submissions.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-gray-500">No submissions found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm text-gray-700">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Submission Details Modal */}
      {showDetails && selectedSubmission && (
        <SubmissionDetailsModal
          submission={selectedSubmission}
          customPage={customPage}
          onClose={() => {
            setShowDetails(false);
            setSelectedSubmission(null);
          }}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      )}
    </div>
  );
};

// Submission Details Modal Component
const SubmissionDetailsModal = ({ 
  submission, 
  customPage, 
  onClose, 
  onStatusChange, 
  onDelete,
  canUpdate,
  canDelete 
}) => {
  const [submissionComments, setSubmissionComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  // Document Inventory State
  const [documentInventory, setDocumentInventory] = useState([]);
  const [documentDate, setDocumentDate] = useState('');
  const [documentFile, setDocumentFile] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Initialize submission comments (only additional comments, not the initial comment)
    if (submission) {
      // Only add submissionComments array, not the initial comment
      // The initial comment is displayed separately
      if (submission.submissionComments && Array.isArray(submission.submissionComments)) {
        setSubmissionComments(submission.submissionComments);
      } else {
        setSubmissionComments([]);
      }
    } else {
      setSubmissionComments([]);
    }
  }, [submission]);

  // Fetch document inventory if enabled
  useEffect(() => {
    if (customPage?.allowDocumentInventory && submission?._id) {
      fetchDocumentInventory();
    }
  }, [customPage, submission?._id]);

  const fetchDocumentInventory = async () => {
    if (!submission?._id) return;
    try {
      const response = await api.get(`/custom-pages/submissions/${submission._id}/document-inventory`);
      if (response.data.success) {
        setDocumentInventory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching document inventory:', error);
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!documentFile || !documentDate) {
      toast.error('Please select a file and document date');
      return;
    }

    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', documentFile);
      formData.append('documentDate', documentDate);

      const response = await api.post(
        `/custom-pages/submissions/${submission._id}/document-inventory`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.success) {
        toast.success('Document uploaded successfully');
        setDocumentDate('');
        setDocumentFile(null);
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
        await fetchDocumentInventory();
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await api.delete(
        `/custom-pages/submissions/${submission._id}/document-inventory/${documentId}`
      );

      if (response.data.success) {
        toast.success('Document deleted successfully');
        await fetchDocumentInventory();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const getFullFileUrl = (relativePath) => {
    if (!relativePath) return '';
    // If it's already a full URL, return as is
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath;
    }
    // Remove leading slash if present
    const cleanPath = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
    // Get base URL without /api
    const baseUrl = API_URL.replace('/api', '');
    return `${baseUrl}/${cleanPath}`;
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await api.post(`/custom-pages/submissions/${submission._id}/comments`, {
        text: newComment.trim()
      });
      
      if (response.data.success) {
        setNewComment('');
        // Refresh submission data
        const updatedResponse = await api.get(`/custom-pages/submissions/${submission._id}`);
        if (updatedResponse.data.success) {
          const updatedSubmission = updatedResponse.data.data;
          // Update comments state (only additional comments, not the initial comment)
          if (updatedSubmission.submissionComments && Array.isArray(updatedSubmission.submissionComments)) {
            setSubmissionComments(updatedSubmission.submissionComments);
          } else {
            setSubmissionComments([]);
          }
          // Update the submission in parent component
          onStatusChange(submission._id, submission.status); // This will trigger a refresh
        }
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFieldValue = (field, value) => {
    if (!value) return '-';

    switch (field.type) {
      case 'upload':
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className="space-y-1">
              {value.map((file, index) => {
                // Handle different file object structures
                const fileUrl = file.fileUrl || file.url || '';
                const fileName = file.fileName || file.name || file.originalName || 'File';
                
                if (!fileUrl) {
                  return (
                    <div key={index} className="text-sm text-gray-500">
                      {fileName} (No URL available)
                    </div>
                  );
                }
                
                return (
                  <div key={index} className="text-sm text-blue-600 hover:text-blue-800">
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                      {fileName}
                    </a>
                  </div>
                );
              })}
            </div>
          );
        }
        return 'No files uploaded';

      case 'masterdata':
        if (value && typeof value === 'object') {
          return value.clientName || 'N/A';
        }
        return value || '-';

      case 'user':
        if (value && typeof value === 'object') {
          return `${value.firstName} ${value.lastName}`;
        }
        return value || '-';

      case 'date':
        return (value);

      case 'multitext':
        return (
          <div className="whitespace-pre-wrap text-sm">
            {value}
          </div>
        );

      default:
        return value;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Submission Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            {customPage && customPage.allowDocumentInventory === true && (
              <button
                onClick={() => setActiveTab('upload')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'upload'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Upload
                {documentInventory.length > 0 && (
                  <span className="ml-2 bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
                    {documentInventory.length}
                  </span>
                )}
              </button>
            )}
            {customPage && customPage.allowExpensesTab === true && (
              <button
                onClick={() => setActiveTab('expenses')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'expenses'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Expenses
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
        <div className="space-y-6">
          {/* Submission Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                submission.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                submission.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                submission.status === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {submission.status}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Submitted By</label>
              <p className="text-sm text-gray-900">
                {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Submitted Date</label>
              <p className="text-sm text-gray-900">{formatDate(submission.createdAt)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Deadline</label>
              <p className="text-sm text-gray-900">
                {submission.deadline ? formatDate(submission.deadline) : '-'}
              </p>
            </div>
          </div>

          {/* Form Data */}
          <div>
            <h3 className="text-lg font-medium mb-4">Form Data</h3>
            <div className="space-y-4">
              {customPage.fields.map((field, index) => {
                // Check if field should be visible based on deadline type and isDeadlineField
                let shouldShowField = true;
                
                if (field.isDeadlineField && submission.deadline?.type) {
                  if (submission.deadline.type === 'personal') {
                    // Personal deadline: Only show to creator
                    const submitterId = submission.submittedBy?._id || submission.submittedBy;
                    shouldShowField = user && (user.id === submitterId || user._id === submitterId);
                  } else if (submission.deadline.type === 'group') {
                    // Group deadline: Only show to users in the same group
                    const submitterGroupId = submission.submittedBy?.groupId || submission.submittedBy?.group?._id;
                    const userGroupId = user?.groupId || user?.group?._id;
                    shouldShowField = submitterGroupId && userGroupId && submitterGroupId.toString() === userGroupId.toString();
                  }
                }
                
                // Skip rendering if field should not be shown
                if (!shouldShowField) {
                  return null;
                }
                
                return (
                  <div key={index} className="border-b border-gray-200 pb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.isDeadlineField && (
                        <span className="ml-2 text-xs text-blue-600 font-normal">(Deadline Field)</span>
                      )}
                    </label>
                    <div className="text-sm text-gray-900">
                      {renderFieldValue(field, submission.data[field.name])}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Comments */}
          <div>
            <h3 className="text-lg font-medium mb-4">Comments</h3>
            <div className="space-y-4">
              {/* Initial Comment */}
              {submission.comment && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      Initial Comment
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(submission.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{submission.comment}</p>
                </div>
              )}

              {/* Existing Comments */}
              {submissionComments.length > 0 && (
                <div className="space-y-3">
                  {submissionComments.map((comment, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          {comment.createdBy?.firstName} {comment.createdBy?.lastName}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatDate(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.text}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="space-y-2">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={submittingComment || !newComment.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {submittingComment ? 'Adding...' : 'Add Comment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        )}

        {activeTab === 'upload' && customPage && customPage.allowDocumentInventory === true && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium mb-4">Document Inventory</h3>
              
              {/* Documents Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Document Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        File Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {documentInventory.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                          No documents uploaded yet
                        </td>
                      </tr>
                    ) : (
                      documentInventory.map((doc, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(doc.documentDate).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {doc.originalName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(doc.uploadedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {doc.uploadedBy?.firstName} {doc.uploadedBy?.lastName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <a
                              href={getFullFileUrl(doc.fileUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 mr-3"
                            >
                              View
                            </a>
                            {canUpdate && (
                              <button
                                onClick={() => handleDeleteDocument(doc._id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                    </tbody>
                  </table>
                </div>

                {/* Upload Form */}
                {canUpdate && (
                  <div className="bg-gray-50 p-4 rounded-lg mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Upload New Document</h4>
                    <form onSubmit={handleDocumentUpload} className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Document Date *
                          </label>
                          <input
                            type="date"
                            value={documentDate}
                            onChange={(e) => setDocumentDate(e.target.value)}
                            required
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            File *
                          </label>
                          <input
                            type="file"
                            onChange={(e) => setDocumentFile(e.target.files[0])}
                            required
                            accept={customPage.documentInventorySettings?.allowedFileTypes?.join(',') || '*'}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Max size: {customPage.documentInventorySettings?.maxFileSize || 10}MB
                        {customPage.documentInventorySettings?.allowedFileTypes && 
                          ` | Allowed types: ${customPage.documentInventorySettings.allowedFileTypes.join(', ')}`
                        }
                      </p>
                      <button
                        type="submit"
                        disabled={uploadingDocument}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          )}

        {activeTab === 'expenses' && customPage && customPage.allowExpensesTab === true && (
          <CustomPageExpensesTab
            submissionId={submission._id}
            customPage={customPage}
            canUpdate={canUpdate}
            formatDate={formatDate}
          />
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          {canUpdate && (
            <select
              value={submission.status}
              onChange={(e) => onStatusChange(submission._id, e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="completed">Completed</option>
            </select>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(submission._id)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Delete
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomPageSubmissions; 